import { useEffect, useState } from "react";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

const FINANCIAL_INSTALLMENTS_CACHE_KEY = "simulador:financial-installments";
const FINANCIAL_INSTALLMENTS_CACHE_TTL = 60 * 60 * 1000;
const MAX_FINANCIAL_INSTALLMENTS_RETRIES = 3;
const FINANCIAL_INSTALLMENTS_RETRY_DELAY = 600;

export function useFinancialInstallments() {
	const [installments, setInstallments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [retryCount, setRetryCount] = useState(0);

	const getCachedInstallments = () => {
		const cached = sessionStorage.getItem(FINANCIAL_INSTALLMENTS_CACHE_KEY);

		if (!cached) {
			return null;
		}

		try {
			const parsedCache = JSON.parse(cached);
			const isExpired = Date.now() - parsedCache.createdAt > FINANCIAL_INSTALLMENTS_CACHE_TTL;

			if (isExpired) {
				sessionStorage.removeItem(FINANCIAL_INSTALLMENTS_CACHE_KEY);
				return null;
			}

			return parsedCache.data;
		} catch (error) {
			sessionStorage.removeItem(FINANCIAL_INSTALLMENTS_CACHE_KEY);
			return null;
		}
	};

	const setCachedInstallments = data => {
		sessionStorage.setItem(
			FINANCIAL_INSTALLMENTS_CACHE_KEY,
			JSON.stringify({
				createdAt: Date.now(),
				data
			})
		);
	};

	const wait = milliseconds => {
		return new Promise(resolve => setTimeout(resolve, milliseconds));
	};

	const formatFinancialInstallments = installmentsData => {
		if (!Array.isArray(installmentsData)) {
			return [];
		}

		return installmentsData.map(installment => ({
			label: `${installment}x`,
			value: installment
		}));
	};

	const requestFinancialInstallments = async () => {
		const {
			accessToken,
			clientId
		} = getAuthParams();

		const requestOptions = {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
				"access_token": accessToken,
				"client_id": clientId
			}
		};

		const response = await fetch(
			process.env.REACT_APP_ESSILOR_API +
				"pacto-rebates-simulador/v1/financeiro/parcelas-disponiveis",
			requestOptions
		);

		if (!response.ok) {
			await throwResponseError(
				response,
				"Não foi possível carregar as parcelas."
			);
		}

		const data = await response.json();

		return data.resultado || data;
	};

	async function fetchFinancialInstallments({ force = false } = {}) {
		if (force) {
			sessionStorage.removeItem(FINANCIAL_INSTALLMENTS_CACHE_KEY);
		}

		if (!force) {
			const cachedInstallments = getCachedInstallments();

			if (cachedInstallments) {
				setInstallments(cachedInstallments);
				setError(null);
				setRetryCount(0);

				return {
					ok: true,
					installments: cachedInstallments,
					fromCache: true
				};
			}
		}

		try {
			setLoading(true);
			setError(null);
			setRetryCount(0);

			let lastError = null;

			for (let attempt = 1; attempt <= MAX_FINANCIAL_INSTALLMENTS_RETRIES; attempt += 1) {
				try {
					setRetryCount(attempt);

					const installmentsData = await requestFinancialInstallments();
					const formattedInstallments = formatFinancialInstallments(installmentsData);

					setInstallments(formattedInstallments);
					setCachedInstallments(formattedInstallments);
					setError(null);
					setRetryCount(0);

					return {
						ok: true,
						installments: formattedInstallments,
						fromCache: false,
						attempt
					};
				} catch (requestError) {
					lastError = requestError;

					if (attempt < MAX_FINANCIAL_INSTALLMENTS_RETRIES) {
						await wait(FINANCIAL_INSTALLMENTS_RETRY_DELAY);
					}
				}
			}

			throw lastError;
		} catch (requestError) {
			setInstallments([]);
			setError(requestError.message);

			return {
				ok: false,
				error: requestError.message,
				retryLimitReached: true
			};
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		fetchFinancialInstallments();
	}, []);

	return {
		installments,
		loading,
		error,
		retryCount,
		maxRetries: MAX_FINANCIAL_INSTALLMENTS_RETRIES,
		fetchFinancialInstallments
	};
}